import { useState } from 'react';
import { Power, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// A merchant that is APPROVED or ACTIVE can use the portal; INACTIVE is blocked at login.
const ACTIVE_STATES = ['ACTIVE', 'APPROVED'];

/**
 * Super-admin / admin control to switch a merchant Active ⇆ Inactive.
 * `card`   → labelled block used on the merchant detail header.
 * `inline` → compact segmented buttons used in the Profile tab (replaces the old read-only field).
 */
export function MerchantStatusToggle({
  merchantId,
  status,
  variant = 'card',
}: {
  merchantId: number;
  status: string;
  variant?: 'card' | 'inline';
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const isActive = ACTIVE_STATES.includes(status);
  const locked = status === 'PENDING' || status === 'REJECTED';

  const setActive = async (active: boolean) => {
    if (busy || locked || active === isActive) return;
    setBusy(true);
    try {
      await api.post(`/merchants/${merchantId}/active`, { active });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['merchant', merchantId] }),
        qc.invalidateQueries({ queryKey: ['merchants'] }),
      ]);
      toast.success(active ? 'Merchant is now Active' : 'Merchant is now Inactive');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not change status');
    } finally {
      setBusy(false);
    }
  };

  const buttons = (
    <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm shrink-0">
      <button
        type="button"
        onClick={() => setActive(true)}
        disabled={busy || locked}
        className={`px-3 py-1.5 flex items-center gap-1 transition disabled:opacity-60 ${
          isActive ? 'bg-emerald-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
        }`}>
        {busy && !isActive ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
        Active
      </button>
      <button
        type="button"
        onClick={() => setActive(false)}
        disabled={busy || locked}
        className={`px-3 py-1.5 flex items-center gap-1 border-l border-slate-300 transition disabled:opacity-60 ${
          !isActive && !locked ? 'bg-rose-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
        }`}>
        {busy && isActive ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
        Inactive
      </button>
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className="space-y-1">
        {buttons}
        {locked && (
          <p className="text-xs text-amber-600">Status is {status} — approve the merchant first.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
        }`}>
        <Power size={16} />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-sm">Account Status</div>
            <div className="text-xs text-slate-500">
              {locked ? (
                <>This merchant is <b>{status}</b>. Approve the merchant before activating.</>
              ) : (
                <>An <b>Inactive</b> merchant cannot log in to the portal.</>
              )}
            </div>
          </div>
          {buttons}
        </div>
      </div>
    </div>
  );
}
